import { useCallback, useEffect, useState } from 'react';
import { Backdrop } from './components/Backdrop.jsx';
import { Navbar } from './components/Navbar.jsx';
import { DisclaimerModal } from './components/DisclaimerModal.jsx';
import { AuthModal } from './components/AuthModal.jsx';
import { FeedbackFab } from './components/FeedbackFab.jsx';
import { BackToTop } from './components/BackToTop.jsx';
import { Hero } from './components/sections/Hero.jsx';
import { Features } from './components/sections/Features.jsx';
import { Roles } from './components/sections/Roles.jsx';
import { AiAssistant } from './components/sections/AiAssistant.jsx';
import { Workflow } from './components/sections/Workflow.jsx';
import { ThemesDemo } from './components/sections/ThemesDemo.jsx';
import { Testimonials } from './components/sections/Testimonials.jsx';
import { Examples } from './components/sections/Examples.jsx';
import { Cta } from './components/sections/Cta.jsx';
import { useAuth } from './hooks/useAuth.js';
import { useExamples } from './hooks/useExamples.js';
import { useI18n } from './hooks/useI18n.js';
import { useScrollFlag } from './hooks/useScrollFlag.js';
import { useSectionObservers } from './hooks/useSectionObservers.js';
import { useTheme } from './hooks/useTheme.js';
import { getToken } from './lib/auth.js';
import { registerNewNodes } from './lib/i18n.js';

// An in-page anchor should scroll the whole section into view, not just the
// element — the sections are full-height panels wrapped in .fp-wrapper.
function scrollToHash(hash) {
  const target = document.querySelector(hash);
  if (!target) return;
  (target.closest('.fp-wrapper') || target).scrollIntoView({ behavior: 'smooth' });
}

export default function App() {
  const { theme, chooseTheme, accentGradient } = useTheme();
  const {
    lang, mode, chooseLang, chooseMode,
    disclaimerOpen, openDisclaimer, closeDisclaimer, disclaimerVisible,
  } = useI18n();
  const { user, loading: authLoading, signIn, signOut } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const scrolled = useScrollFlag(40);
  const showBackToTop = useScrollFlag(600);
  // The sections render with this component, so the DOM they observe exists by
  // the time effects run — unlike the old page, which had to await nine fetches.
  const activeId = useSectionObservers(true);

  const examples = useExamples(user ? getToken() : null, !authLoading);

  // Tell the translator about the tree React has just mounted. Its own
  // MutationObserver would find it too; doing it explicitly removes the
  // dependence on observer timing for the initial render.
  useEffect(() => {
    registerNewNodes();
  }, []);

  // Delegated smooth scroll for in-page anchors inside the content (the hero
  // CTA, the welcome banner's "Browse LiveDecks"). The nav has its own handler
  // because it must also close the mobile menu, so anchors there are skipped.
  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest?.('#content a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute('href');
      if (hash === '#') return;
      e.preventDefault();
      scrollToHash(hash);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleAuthenticated = useCallback((token) => signIn(token), [signIn]);

  return (
    <>
      <Backdrop />

      <Navbar
        scrolled={scrolled}
        activeId={activeId}
        theme={theme}
        accentGradient={accentGradient}
        onChooseTheme={chooseTheme}
        lang={lang}
        displayMode={mode}
        onChooseLang={chooseLang}
        onChooseMode={chooseMode}
        onOpenDisclaimer={openDisclaimer}
        disclaimerVisible={disclaimerVisible}
        user={user}
        authLoading={authLoading}
        onSignIn={() => setAuthOpen(true)}
        onSignOut={signOut}
        onNavigate={scrollToHash}
      />

      <DisclaimerModal open={disclaimerOpen} lang={lang} mode={mode} onClose={closeDisclaimer} />

      <main id="content">
        <Hero user={user} />
        <Features />
        <Roles />
        <AiAssistant />
        <Workflow />
        <ThemesDemo onChoose={chooseTheme} />
        <Testimonials />
        <Examples lectures={examples} />
        <Cta />
      </main>

      <FeedbackFab />
      <BackToTop show={showBackToTop} />

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onAuthenticated={handleAuthenticated}
      />
    </>
  );
}
